import { formatCurrency } from '@if/core/utils'
import { ActionLink, Caption, DataField, FieldValue, Icon, MetaValue } from '@if/mui'
import { Stack } from '@mui/material'
import { useMonolithBaseUrl } from '@src/services/monolith-hook'
import { InteracPossibleMatch } from '@src/types/interac'
import { StickyNote } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface InteracCandidateRowProps {
  candidate: InteracPossibleMatch
  transferAmount: number
  onOpenNotes: (candidate: InteracPossibleMatch) => void
}

const InteracCandidateRow = ({ candidate, transferAmount, onOpenNotes }: InteracCandidateRowProps) => {
  const { t } = useTranslation()
  const monolithBaseUrl = useMonolithBaseUrl()

  const coBorrower =
    candidate.coapplicantFirstName && candidate.coapplicantLastName
      ? `${candidate.coapplicantFirstName} ${candidate.coapplicantLastName}`
      : null

  const arrearsHint =
    candidate.lateAmount === 0
      ? t('interacWire.candidates.noArrears')
      : candidate.lateAmount === transferAmount
        ? t('interacWire.candidates.lateEqualsTransfer')
        : null

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{ width: '100%', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' } }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <MetaValue>
          {t(`enum.customerType.${candidate.ppaCustomerTypeName}`)} · {candidate.referenceNumber}
        </MetaValue>
        <FieldValue>{`${candidate.applicantFirstName} ${candidate.applicantLastName}`}</FieldValue>
        {coBorrower && <Caption>{t('interacWire.candidates.coBorrower', { name: coBorrower })}</Caption>}
        {arrearsHint && <Caption>{arrearsHint}</Caption>}
        {candidate.notes.length > 0 && (
          <ActionLink onClick={() => onOpenNotes(candidate)} sx={{ width: 'fit-content' }}>
            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
              <Icon icon={StickyNote} size="sm" />
              <span>{t('interacWire.candidates.notes', { count: candidate.notes.length })}</span>
            </Stack>
          </ActionLink>
        )}
      </Stack>

      <Stack direction="row" spacing={6}>
        <Stack direction="row" spacing={4}>
          <DataField
            label={t('interacWire.lateAmount')}
            value={formatCurrency(candidate.lateAmount)}
            sx={{ textAlign: 'right' }}
          />
          <DataField
            label={t('interacWire.balanceAmount')}
            value={formatCurrency(candidate.loanBalance)}
            sx={{ textAlign: 'right' }}
          />
        </Stack>

        <Stack direction="row" spacing={3} sx={{ flexShrink: 0, alignItems: 'flex-start' }}>
          <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
            <ActionLink href={`${monolithBaseUrl}/admin/ppa/customer.aspx?id=${candidate.loanId}`} target="_blank">
              {t('interacWire.candidates.customer')}
            </ActionLink>
            <ActionLink href={`${monolithBaseUrl}/admin/customer/${candidate.loanId}`} target="_blank">
              {t('interacWire.candidates.calendar')}
            </ActionLink>
            <ActionLink
              href={`${monolithBaseUrl}/admin/ppa/customer.aspx?id=${candidate.loanId}&tab=config-ppa`}
              target="_blank"
            >
              {t('interacWire.candidates.configPPA')}
            </ActionLink>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

export default InteracCandidateRow
